import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface FleetProvider {
  id: string;
  company_id: string;
  name: string;
  type: string;
  cnpj?: string;
  status: string;
  contact_info: any;
  address: any;
  rating?: number;
  services_count?: number;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
}

export function useFleetProviders(companyId?: string) {
  return useQuery({
    queryKey: ["fleetProviders", companyId],
    queryFn: async () => {
      if (!companyId) return [];
      const { data, error } = await supabase
        .from("fleet_providers")
        .select("*")
        .eq("company_id", companyId)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!companyId,
  });
}

export function useCreateFleetProvider(companyId: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (providerData: Omit<FleetProvider, 'id' | 'created_at' | 'updated_at' | 'company_id'>) => {
      const { data, error } = await supabase
        .from("fleet_providers")
        .insert([{ ...providerData, company_id: companyId }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fleetProviders", companyId] });
      toast({
        title: "Sucesso",
        description: "Fornecedor criado com sucesso.",
      });
    },
    onError: (error) => {
      console.error("Error creating provider:", error);
      toast({
        title: "Erro",
        description: "Erro ao criar fornecedor.",
        variant: "destructive",
      });
    },
  });
}

export function useUpdateFleetProvider(companyId: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...providerData }: Partial<FleetProvider> & { id: string }) => {
      const { data, error } = await supabase
        .from("fleet_providers")
        .update(providerData)
        .eq("id", id)
        .eq("company_id", companyId)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fleetProviders", companyId] });
      toast({
        title: "Sucesso",
        description: "Fornecedor atualizado com sucesso.",
      });
    },
    onError: (error) => {
      console.error("Error updating provider:", error);
      toast({
        title: "Erro",
        description: "Erro ao atualizar fornecedor.",
        variant: "destructive",
      });
    },
  });
}

export function useDeleteFleetProvider(companyId: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (providerId: string) => {
      const { error } = await supabase
        .from("fleet_providers")
        .delete()
        .eq("id", providerId)
        .eq("company_id", companyId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fleetProviders", companyId] });
      toast({
        title: "Sucesso",
        description: "Fornecedor excluído com sucesso.",
      });
    },
    onError: (error) => {
      console.error("Error deleting provider:", error);
      toast({
        title: "Erro",
        description: "Erro ao excluir fornecedor.",
        variant: "destructive",
      });
    },
  });
}